import { CSSProperties, useMemo } from 'react';
import { SeatPosition } from 'hooks/useEllipticalPositions';
import { useOrientation } from 'hooks/useOrientation';

import type { WaitingRegistrant } from 'components/SitGoWaiting/sitGoWaitingState';

import { LandscapeOpponent } from './LandscapeOpponent';
import { PortraitOpponent } from './PortraitOpponent';

type WaitingSeatProps = {
  registrant: WaitingRegistrant;
  seatId: number;
  currentPlayerSeatId: number;
  position: SeatPosition;
  cardPosition: any;
  holeCardCount: number;
  isRotating?: boolean;
};

// Inherited by the seat card: a waiting seat takes no taps, so where it meets
// the felt's waiting panel (which it paints over) the panel's buttons still
// receive them.
const WRAPPER: CSSProperties = { display: 'contents', pointerEvents: 'none' };
const NO_CARDS: string[] = [];

/**
 * EN-11257 (release.sit-go-seated-waiting): a Sit & Go registrant in their
 * drawn seat while the table fills. Drawn by the occupied-opponent seat modes,
 * so it carries the same avatar, name and stack card as a seated player — but
 * it is not a seat in the table feed: no cards, no action, no turn, no
 * winner state, and nothing on it takes a tap. The viewer's own registrant is
 * drawn the same way; they are not seated (currentPlayer stays unseated), so
 * none of a seated player's controls appear until the game starts.
 */
export const WaitingSeat = ({
  registrant,
  seatId,
  currentPlayerSeatId,
  position,
  cardPosition,
  holeCardCount,
  isRotating = false
}: WaitingSeatProps) => {
  const isPortrait = useOrientation() === 'portrait';
  // Shaped like a players[n] entry, which is what the seat card reads.
  const seat = useMemo(
    () => ({
      ID: registrant.playerId,
      GUID: registrant.playerId,
      displayName: registrant.displayName,
      avatar: registrant.avatar,
      stack: registrant.stack
    }),
    [registrant]
  );

  const props = {
    seat,
    seatId,
    currentPlayerSeatId,
    isFaceCardUp: false,
    position,
    cards: NO_CARDS,
    hasAction: false,
    playerAction: '',
    displayAction: '',
    actionColor: null,
    isWinner: false,
    isTurn: false,
    cardPosition,
    holeCardCount,
    isRotating
  };

  return (
    <div style={WRAPPER} data-sit-go-waiting-seat={seatId}>
      {isPortrait ? <PortraitOpponent {...props} /> : <LandscapeOpponent {...props} />}
    </div>
  );
};
